import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, LogOut, Shield, Database, AlertCircle, CheckCircle, User } from 'lucide-react';
import { localDB } from '@/lib/storage';

export default function Settings() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
      setMessage({ type: 'error', text: 'Failed to sign out' });
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    setMessage(null);
    try {
      if (localDB.isInitialized()) {
        await localDB.clearAll();
        setMessage({ type: 'success', text: 'Local cache cleared successfully' });
      } else {
        setMessage({ type: 'error', text: 'Local database not initialized' });
      }
    } catch (err) {
      console.error('Clear cache error:', err);
      setMessage({ type: 'error', text: 'Failed to clear local cache' });
    } finally {
      setClearingCache(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <header style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => navigate('/vault')}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              color: '#6b7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
            Settings
          </h1>
        </div>
      </header>

      <main style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px'
      }}>
        {message && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={20} color="#22c55e" />
            ) : (
              <AlertCircle size={20} color="#ef4444" />
            )}
            <span style={{
              fontSize: '14px',
              color: message.type === 'success' ? '#166534' : '#991b1b'
            }}>
              {message.text}
            </span>
          </div>
        )}

        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px'
            }}>
              <User size={20} color="#3b82f6" />
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Account Information
            </h2>
          </div>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 500,
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                Email Address
              </label>
              <p style={{
                padding: '12px',
                fontSize: '14px',
                color: '#374151',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                margin: 0
              }}>
                {user?.email || 'Not available'}
              </p>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 500,
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                User ID
              </label>
              <p style={{
                padding: '12px',
                fontSize: '12px',
                fontFamily: 'monospace',
                color: '#374151',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                margin: 0,
                wordBreak: 'break-all'
              }}>
                {user?.id || 'Not available'}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px'
            }}>
              <Shield size={20} color="#3b82f6" />
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Security
            </h2>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <Shield size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1e40af',
                  marginBottom: '6px'
                }}>
                  Zero-Knowledge Encryption
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: '#1e40af',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Your vault is protected with end-to-end encryption. Your master password is never stored or transmitted to our servers. All encryption and decryption happens locally in your browser.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px'
            }}>
              <Database size={20} color="#3b82f6" />
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Data Management
            </h2>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                Clear Local Cache
              </h3>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                Remove locally cached data from your browser
              </p>
            </div>
            <button
              onClick={handleClearCache}
              disabled={clearingCache}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151',
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: clearingCache ? 'not-allowed' : 'pointer',
                opacity: clearingCache ? 0.6 : 1
              }}
            >
              {clearingCache ? 'Clearing...' : 'Clear Cache'}
            </button>
          </div>

          <div style={{
            padding: '12px',
            fontSize: '12px',
            color: '#6b7280',
            backgroundColor: '#fefce8',
            border: '1px solid #fde047',
            borderRadius: '8px'
          }}>
            <strong>Note:</strong> Clearing cache will remove locally stored data. Your encrypted data on the server remains safe.
          </div>
        </div>

        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              backgroundColor: '#fef2f2',
              borderRadius: '8px'
            }}>
              <LogOut size={20} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Sign Out
            </h2>
          </div>

          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '16px'
          }}>
            Sign out of your account. You'll need to sign in again and unlock your vault to access your data.
          </p>

          <button
            onClick={handleSignOut}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: loading ? '#9ca3af' : '#ef4444',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#dc2626')}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#ef4444')}
          >
            <LogOut size={16} />
            {loading ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>

        <div style={{
          marginTop: '24px',
          padding: '16px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#9ca3af'
        }}>
          Secure Vault v1.0.0
        </div>
      </main>
    </div>
  );
}
